import { useCallback, useEffect, useRef, useState } from 'react';

import AssistantRagStorageService from '@shared/services/AssistantRagStorageService';
import type { IAssistantRagVectorStorage } from '@shared/interfaces/assistantRagStorage';

// Poll interval used while any RAG item is in INDEXING_PENDING. Stops as soon
// as the last pending item resolves to READY or INDEXING_FAILED.
const POLL_INTERVAL_MS = 2500;

export interface UseAssistantRagStorageResult {
  items: IAssistantRagVectorStorage[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Loads assistant RAG storage items and keeps them fresh.
 *
 * The hook polls on a soft interval while there is at least one
 * INDEXING_PENDING item so the inspector can transition rows from amber
 * (indexing) to green (READY) or red (FAILED) without a page reload.
 */
export const useAssistantRagStorage = (
  brandingId: string | undefined,
): UseAssistantRagStorageResult => {
  const [items, setItems] = useState<IAssistantRagVectorStorage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const mountedRef = useRef(true);
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchOnce = useCallback(async () => {
    try {
      const data =
        await AssistantRagStorageService.getAssistantRagStorageItems();
      if (!mountedRef.current) {
        return;
      }
      setItems(data);
      setError(null);
    } catch (fetchError) {
      if (!mountedRef.current) {
        return;
      }
      console.error(
        `Failed to load assistant RAG items: ${(fetchError as Error).message}`,
        { brandingId },
      );
      setError(fetchError as Error);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [brandingId]);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    void fetchOnce();
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [fetchOnce]);

  useEffect(() => {
    const hasPending = items.some(
      (item) => item.status === 'INDEXING_PENDING',
    );
    if (!hasPending) {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      return;
    }
    pollTimerRef.current = setTimeout(() => {
      void fetchOnce();
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [items, fetchOnce]);

  return { items, loading, error, refetch: fetchOnce };
};
